import { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getDisbalanceUnits,
  validResidualDisbalance,
  ValueOrNull,
} from "../../../../utils/helpers/tolerance/GeneralHelper.js";
import { useUnits } from "../../../../hooks/useMeasureInUnits.jsx";
import { toFixed } from "../../../../utils/helpers/NumberHelper.js";
import { HelpOutput } from "../../../Inputs/HelpDisplay/HelpOutput.tsx";
import UnitsRadioGroup from "../../../Inputs/HelpDisplay/UnitsRadioGroup.jsx";
import ResultBox from "../../../Styled/Box/ResultBox.jsx";
import InputStack from "../../../Styled/Stack/InputStack.jsx";

const ResidualDisbalanceDisplayer = ({
  isDualPlane,
  residualDisbalance,
  specificResidualDisbalance,
  residualDisbalancePerPlaneA,
  residualDisbalancePerPlaneB,
  mountType,
  title,
  totalDisbalanceHelpButtonText,
}) => {
  const { t } = useTranslation();
  const disbalanceUnits = useMemo(() => getDisbalanceUnits(t), [t]);
  const { unitLabel, unit, handleUnitChange } = useUnits(
    null,
    disbalanceUnits[0]
  );

  const roundedSpecificResidualDisbalance = useMemo(
    () => ValueOrNull(toFixed(specificResidualDisbalance)),
    [specificResidualDisbalance]
  );

  const roundedResidualDisbalance = useMemo(
    () => ValueOrNull(toFixed(residualDisbalance * unit.value)),
    [residualDisbalance, unit.value]
  );

  const validResidualDisbalancePerPlaneA = useMemo(
    () =>
      ValueOrNull(
        toFixed(
          validResidualDisbalance(
            mountType,
            residualDisbalance,
            residualDisbalancePerPlaneA
          ) * unit.value
        )
      ),
    [mountType, residualDisbalance, residualDisbalancePerPlaneA, unit.value]
  );
  const validResidualDisbalancePerPlaneB = useMemo(
    () =>
      ValueOrNull(
        toFixed(
          validResidualDisbalance(
            mountType,
            residualDisbalance,
            residualDisbalancePerPlaneB
          ) * unit.value
        )
      ),
    [mountType, residualDisbalance, residualDisbalancePerPlaneB, unit.value]
  );

  return (
    <>
      <Typography variant="h4" sx={{ my: 3 }}>
        {title}
      </Typography>
      <Typography variant="h6">
        {t("units.disbalance.unitSelection")}
      </Typography>
      <UnitsRadioGroup
        value={unit}
        onChange={handleUnitChange}
        options={disbalanceUnits}
      />
      <ResultBox>
        <Typography variant="h6">
          {t(
            "shared.disbalance.components.residualDisbalanceDisplayer.components.totalDisbalance.title"
          )}
        </Typography>
        <Box sx={{ my: 2, width: "calc(100%)", paddingRight: 2 }}>
          <HelpOutput
            label={
              <div>
                U<sub>per</sub>, {unitLabel}
              </div>
            }
            endAdornment={unitLabel}
            helpButtonText={totalDisbalanceHelpButtonText}
          >
            {roundedResidualDisbalance}
          </HelpOutput>
        </Box>
        <Typography variant="h6">
          {t(
            "shared.disbalance.components.residualDisbalanceDisplayer.components.specificDisbalance.title"
          )}
        </Typography>
        <Box sx={{ my: 2, width: "calc(100%)", paddingRight: 2 }}>
          <HelpOutput
            label={
              <div>
                e<sub>per</sub>, {t("units.length.mkm")}
              </div>
            }
            endAdornment={t("units.length.mkm")}
            helpButtonText={t(
              "shared.disbalance.components.residualDisbalanceDisplayer.components.specificDisbalance.helpButtonText"
            )}
          >
            {roundedSpecificResidualDisbalance}
          </HelpOutput>
        </Box>
        {isDualPlane && (
          <Box sx={{ mt: 2, width: "calc(100%)", paddingRight: 2 }}>
            <Typography variant="h6">
              {t(
                "shared.disbalance.components.residualDisbalanceDisplayer.components.allowedDisbalanceByPlanes.title"
              )}
            </Typography>
            <InputStack>
              <HelpOutput
                label={
                  <div>
                    U<sub>per A</sub>, {unitLabel}
                  </div>
                }
                endAdornment={unitLabel}
                helpButtonText={t(
                  "shared.disbalance.components.residualDisbalanceDisplayer.components.disbalancePerPlane.helpButtonText",
                  {
                    plane: "A",
                  }
                )}
              >
                {validResidualDisbalancePerPlaneA}
              </HelpOutput>
              <HelpOutput
                label={
                  <div>
                    U<sub>per B</sub>, {unitLabel}
                  </div>
                }
                endAdornment={unitLabel}
                helpButtonText={t(
                  "shared.disbalance.components.residualDisbalanceDisplayer.components.disbalancePerPlane.helpButtonText",
                  {
                    plane: "B",
                  }
                )}
              >
                {validResidualDisbalancePerPlaneB}
              </HelpOutput>
            </InputStack>
          </Box>
        )}
      </ResultBox>
    </>
  );
};

export default ResidualDisbalanceDisplayer;
